<template>
  <base-modal-component class="u-typography-color" >
    <template v-slot:header>
      <h4 class="u-typography-color md-title">
        Question {{ question.ID }}
      </h4>
      <generic-button
        icon
        transparent
        @click="close()"
      >
        <md-icon>
          clear
        </md-icon>
      </generic-button>
    </template>

    <template v-slot:body>
      <p
        v-if="error && error.messages"
        class="md-error"
      >
        {{ error.messages }}
      </p>
      <form
        novalidate
      >
        <div v-if="dep !== -1" class="c-config-input-type c-config-input-type--column">
          <div class="c-config-input-info">
            Dependency: {{ dep }}
          </div>
          <div class="c-hint">Questions that have a dependency are invisible until triggered</div>
        </div>
        <div class="c-config-input-type">
          <md-field>
            <label for="type">Type</label>
            <md-select v-model="question.type" name="type" id="type">
              <md-option value="RADIO">
                  Radio
              </md-option>
              <md-option value="YESNO">
                  Yes/No
              </md-option>
              <md-option value="TEXT">
                  Text
              </md-option>
            </md-select>
          </md-field>
        </div>
        <div class="c-config-input-type c-config-input-type--column">
          <md-field :class="getValidationClass($v.question.question)">
            <label for="question">Question</label>
            <md-textarea
              v-model="question.question"
              v-focus
              id="question"
              name="question"
              type="text"
              class="md-input"
              autocomplete="off"
              md-autogrow
            />
            <span
              v-if="!$v.question.question.required"
              class="md-error"
            >Question is required</span>
          </md-field>
          <div v-if="question.question.trim() === ''" class="c-hint">
            Will use fallback if empty
          </div>
          <error-response-messages :field-name="'message'" />
        </div>
        <template v-if="question.type === 'RADIO'">
          <div
            v-for="(answer, index) in question.answers"
            :key="'templateindex' + index"
            class="u-flex"
          >
            <div
              class="c-config-input-type c-config-input-type--column c-flex-1"
            >
              <div class="c-config-input-type c-config-input-type--column">
                <md-field class="u-mb-md">
                  <label :for="'message' + index">Message</label>
                  <md-textarea
                    v-model="question.answers[index].message"
                    :id="'message' + index"
                    :name="'message' + index"
                    autocomplete="off"
                    md-autogrow
                  />
                </md-field>
                <div v-if="question.answers[index].message.trim() === ''" class="c-hint">
                  Will use fallback if empty
                </div>
              </div>
              <div class="c-config-input-type">
                <div class="c-config-input-info">
                  Comment mandatory?
                </div>

                <div class="c-config-input-actions">
                  <md-radio v-model="question.answers[index].mandatoryAnswer" :value="true">
                    Yes
                  </md-radio>
                  <md-radio v-model="question.answers[index].mandatoryAnswer" :value="false">
                    No
                  </md-radio>
                </div>
              </div>
              <div class="c-config-input-type" v-if="question.answers[index].mandatoryAnswer">
                <md-field>
                  <label :for="'minCharacters' + index">Minimum characters for comment</label>
                  <md-input
                    v-model="question.answers[index].minCharacters"
                    :id="'minCharacters' + index"
                    :name="'minCharacters' + index"
                    type="text"
                    @keydown="filterNonNumber"
                    class="md-input"
                    autocomplete="off"
                  />
                </md-field>
              </div>
              <div class="c-config-input-type u-flex-column">
                <md-field>
                  <label :for="'dependency' + index">Triggers</label>
                  <md-select v-model="question.answers[index].dependency" :name="'dependency' + index" :id="'dependency' + index" :disabled="questionsEligibleForDependency.length === 0">
                    <md-option value="" ></md-option>
                    <md-option v-for="q in questionsEligibleForDependency" :key="q.ID" :value="q.ID">
                        {{ q.ID }} - {{ q.question }}
                    </md-option>
                  </md-select>
                </md-field>
                <div v-if="questionsEligibleForDependency.length === 0">No dependencies available</div>
              </div>
            </div>
            <div class="u-flex-column">
              <generic-button
                icon
                transparent
                text-inherit
                @click="moveAnswerUp(index)"
              >
                <md-tooltip md-direction="top">Move up</md-tooltip>
                <md-icon>arrow_upward</md-icon>
              </generic-button>
              <generic-button
                icon
                transparent
                text-inherit
                @click="moveAnswerDown(index)"
              >
                <md-tooltip md-direction="top">Move down</md-tooltip>
                <md-icon>arrow_downward</md-icon>
              </generic-button>
              <generic-button
                icon
                transparent
                @click="deleteAnswer(index)"
              >
                <img src="../../../../assets/delete-button.svg" alt="">
              </generic-button>
            </div>
          </div>
          <generic-button
            variation="grey"
            class="u-mr-md"
            @click="addNewAnswer"
          >
            Add new answer
          </generic-button>
        </template>
        <template v-if="question.type === 'TEXT'">
          <div class="c-config-input-type">
            <md-field>
              <label for="qMinCharacters">Minimum characters for comment</label>
              <md-input
                v-model="question.minCharacters"
                name="qMinCharacters"
                type="text"
                @keydown="filterNonNumber"
                class="md-input"
                autocomplete="off"
              />
            </md-field>
          </div>
        </template>
        <div class="md-error"  v-if="radioValidatorTouched && shouldCheckRadio && !radioValid">
          Radio type must have answers
        </div>
      </form>
    </template>
    <template v-slot:footer>
      <div class="u-mr-xs">
        <generic-button
          variation="grey"
          @click="close"
        >
          Close
        </generic-button>
      </div>
      <generic-button
        variation="red"
        @click="validateForm"
      >
        Continue
      </generic-button>
    </template>
  </base-modal-component>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { required } from "vuelidate/lib/validators";
import GenericButton from '@/shared/buttons/GenericButton'
import ErrorResponseMessages from '@/shared/error-response-messages/ErrorResponseMessages'
import BaseModalComponent from '@/shared/modal/BaseModalComponent'
import { validationBaseMixin } from '@/mixins/ValidationBaseMixin'

export default {
  name: 'CreateQuestionModal',
  components: {
    GenericButton,
    ErrorResponseMessages,
    BaseModalComponent
  },
  mixins: [validationBaseMixin],
  props: {
    propQuestion: {
      type: Object,
      required: true,
    },
    questionsEligibleForDependency: {
      type: Array,
      required: true
    },
    dep: {
      type: Number,
      required: true
    },
    successFunction: {
      type: Function,
      required: true
    }
  },

  data() {
    return {
      messages: {
        error: null,
        success: null,
      },
      radioValidatorTouched: false,
      question: JSON.parse(JSON.stringify(this.propQuestion))
    };
  },
  computed: {
    ...mapGetters("response", ["success", "error"]),
    shouldCheckRadio() {
      return this.question.type === 'RADIO';
    },
    radioValid() {
      return this.question.answers.length !== 0;
    }
  },

  validations: {
    question: {
      question: {
        required
      },
    },
  },

  methods: {
    ...mapActions("response", ["handleSuccess", "handleError", "resetError"]),

    validateForm () {
      this.$v.$touch();
      this.radioValidatorTouched = true;

      if (this.shouldCheckRadio) {
        if (this.radioValid && !this.$v.$invalid) {
          this.successFunction(this.question);
          this.close();
        }
      } else if (!this.$v.$invalid) {
        this.successFunction(this.question);
        this.close();
      }
    },

    close() {
      this.$emit("close");
    },

    addNewAnswer() {
      if (!this.question.answers) {
        this.question.answers = [];
      }
      this.question.answers.push({
          id: this.question.answers.reduce((max, cur) => cur.id > max.id ? cur : max, { id: -1 }).id + 1,
          message: "",
          mandatoryAnswer: false,
          minCharacters: '',
          dependency: undefined
      });
    },

    deleteAnswer(index) {
      if (this.question.answers) {
        this.question.answers.splice(index, 1);
      }
    },

    moveAnswerUp(answerIdx) {
      if (answerIdx === 0) {
        return;
      }
      this.moveAnswerTo(answerIdx - 1, answerIdx);
    },

    moveAnswerDown(answerIdx) {
      if (answerIdx === this.question.answers.length - 1) {
        return;
      }
      this.moveAnswerTo(answerIdx + 1, answerIdx);
    },

    moveAnswerTo(newIdx, oldIdx) {
      const q = this.question;
      [q.answers[newIdx], q.answers[oldIdx]] = [q.answers[oldIdx], q.answers[newIdx]];
      this.question = { ...q };
    },

    filterNonNumber(event) {
      const allowedChars = /^[0-9]$/;
      const allowedKeys = [
        "Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight",
        "ArrowUp", "ArrowDown", "Control", "Shift", "Alt", "Meta"
      ];

      if (!allowedChars.test(event.key) && !allowedKeys.includes(event.key)) {
        if (event.ctrlKey || event.altKey || event.metaKey) {
          return;
        }
        event.preventDefault();
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.c-config-textarea {
  width: 100%; margin-left: 10px;resize: none;
}

.c-flex-1 {
  flex: 1;
}

.c-hint {
  font-size: 12px;
  color: $grey;
}
</style>
